import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Button, message, DatePicker, Select, Row, Col } from "antd";
import API from "../../api";
import moment from "moment";

const EmployeeDrawer = ({ open, setOpen, data, isEdit, onSubmit }) => {
    const [form] = Form.useForm();
    const [isLoading, setLoading] = useState(false);
    const [stores, setStores] = useState([]);
    const getStore = async () => {
        try {
            const response = await API.get("/stores");
            setStores(response?.data || []);
        } catch (err) {
            console.error("Error fetching stores:", err);
            message.error("Failed to load stores.");
        }
    };


    useEffect(() => {
        if (open) {
            getStore();
            if (isEdit && data) {
                form.setFieldsValue({
                    ...data,
                    storeId:data.storeId._id || null,
                    doj: data.doj ? moment(data.doj) : null,
                });
            } else {
                form.resetFields();
            }
        }
    }, [open, isEdit, data, form]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            let response;
            const payload = {
                ...values,
                doj: values.doj ? values.doj.toISOString() : null,
            };
            if (isEdit) {
                response = await API.put(`/employee/${data?._id}`, payload);
                message.success("Employee updated successfully!");
                onSubmit({ ...payload, _id: data?._id });
            } else {
                response = await API.post(`/employee`, payload);
                message.success("Employee added successfully!");
                onSubmit(response?.data);
            }
            setOpen(false);
        } catch (err) {
            console.error(err);
            message.error(err.response?.data?.message || "Failed to process the request.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer
            title={isEdit ? "Edit Employee" : "Add Employee"}
            width={700}
            placement="right"
            onClose={() => setOpen(false)}
            open={open}
        >
            <Form layout="vertical" form={form} onFinish={onFinish} autoComplete="off">
                {isEdit && (
                    <Form.Item name="_id" hidden>
                        <Input type="hidden" />
                    </Form.Item>
                )}

                <Form.Item
                    label="Select Store"
                    name="storeId"
                    rules={[
                        { required: true, message: "Please enter the Store." },
                    ]}
                >
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Store"
                        allowClear
                    >
                        {stores.map((store) => (
                            <Select.Option key={store._id} value={store._id}>
                                {store.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Name"
                            name="name"
                            rules={[{ required: true, message: "Please enter the employee name." }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Email"
                            name="email"
                            rules={[
                                { required: true, message: "Please enter the email." },
                                { type: "email", message: "Please enter a valid email." },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Phone"
                            name="phone"
                            rules={[{ required: true, message: "Please enter the phone number." }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Date of Joining"
                            name="doj"
                            rules={[{ required: true, message: "Please select the date of joining." }]}
                        >
                            <DatePicker style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Designation"
                            name="designation"
                            rules={[{ required: true, message: "Please select the designation." }]}
                        >
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Select Designation"
                                allowClear
                            >
                                <Select.Option value="Manager">Manager</Select.Option>
                                <Select.Option value="General Manager">General Manager</Select.Option>
                                <Select.Option value="Accountant">Accountant</Select.Option>
                                <Select.Option value="Sales Manager">Sales Manager</Select.Option>
                                <Select.Option value="Reception">Reception</Select.Option>
                                <Select.Option value="Office Assistant">Office Assistant</Select.Option>
                                <Select.Option value="Sales Boy">Sales Boy</Select.Option>
                                <Select.Option value="Sales Girl">Sales Girl</Select.Option>
                                <Select.Option value="Technician">Technician</Select.Option>
                                <Select.Option value="Logistics Department">Logistics Department</Select.Option>
                                <Select.Option value="Office Cleaner">Office Cleaner</Select.Option>
                                <Select.Option value="Others">Others</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Salary"
                            name="salary"
                            rules={[
                                { required: true, message: "Please enter the salary." },
                                {
                                    validator: (_, value) => {
                                        if (value === undefined || value === null || value === '') {
                                            return Promise.resolve();
                                        }
                                        const numValue = Number(value);
                                        if (isNaN(numValue) || numValue < 0) {
                                            return Promise.reject(new Error('Salary must be a positive number.'));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Input type="number" min="0" placeholder="Enter salary amount" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label="Details"
                            name="details"
                        >
                            <Input.TextArea rows={4} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item>
                    <Button type="primary" block htmlType="submit" loading={isLoading}>
                        {isEdit ? "Update Employee" : "Add Employee"}
                    </Button>
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default EmployeeDrawer;